import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Send, X } from 'lucide-react';

/**
 * Newsletter broadcast modal — compose message or attach existing article,
 * with mandatory test-send preview and red confirmed broadcast.
 *
 * Parent owns all state; this is a controlled presentational component.
 */
const NewsletterBroadcastModal = ({
  open,
  onClose,
  articles = [],
  subscriberCount,
  mode,
  setMode,
  subject,
  setSubject,
  body,
  setBody,
  articleId,
  setArticleId,
  testEmail,
  setTestEmail,
  isSending,
  onSend,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="newsletter-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Send Newsletter</h2>
            <p className="text-sm text-slate-500">
              {subscriberCount === null
                ? 'Loading subscriber count…'
                : `${subscriberCount} subscriber${subscriberCount !== 1 ? 's' : ''} with marketing consent`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
            data-testid="close-newsletter-modal"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div className="flex gap-2 p-1 bg-slate-100 rounded-lg" role="tablist">
            <button
              onClick={() => setMode('compose')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'compose' ? 'bg-white text-slate-900 shadow' : 'text-slate-600'}`}
              data-testid="newsletter-mode-compose"
              type="button"
            >
              Compose Message
            </button>
            <button
              onClick={() => setMode('article')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'article' ? 'bg-white text-slate-900 shadow' : 'text-slate-600'}`}
              data-testid="newsletter-mode-article"
              type="button"
            >
              Attach Existing Article
            </button>
          </div>

          {mode === 'compose' && (
            <>
              <div>
                <Label htmlFor="nl-subject" className="text-sm font-medium text-slate-700">Subject</Label>
                <Input
                  id="nl-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., April Research Update from Genora Peptides"
                  className="mt-1"
                  data-testid="newsletter-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="nl-body" className="text-sm font-medium text-slate-700">Body (HTML supported)</Label>
                <textarea
                  id="nl-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={10}
                  placeholder="<p>Hi researchers,</p><p>This month we're releasing...</p>"
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono resize-y focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="newsletter-body-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tip: plain HTML tags work (e.g., &lt;p&gt;, &lt;strong&gt;, &lt;a href="..."&gt;). Avoid inline &lt;style&gt; tags — use inline style attributes.
                </p>
              </div>
            </>
          )}

          {mode === 'article' && (
            <div>
              <Label htmlFor="nl-article" className="text-sm font-medium text-slate-700">Select Article</Label>
              <select
                id="nl-article"
                value={articleId}
                onChange={(e) => setArticleId(e.target.value)}
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                data-testid="newsletter-article-select"
              >
                <option value="">-- Choose a published article --</option>
                {articles.filter(a => a.status === 'published').map(a => (
                  <option key={a.post_id} value={a.post_id}>{String(a.title || 'Untitled').trim()}</option>
                ))}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                The email will include the article's summary + a "Read the full article" button linking to the blog.
              </p>
            </div>
          )}

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
            <Label htmlFor="nl-test" className="text-sm font-medium text-amber-900">
              Test send (recommended before blasting)
            </Label>
            <div className="flex gap-2">
              <Input
                id="nl-test"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
                className="flex-1"
                data-testid="newsletter-test-email-input"
              />
              <Button
                onClick={() => onSend({ isTest: true })}
                disabled={isSending || !testEmail.trim()}
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                data-testid="newsletter-send-test-btn"
              >
                {isSending ? 'Sending…' : 'Send Test'}
              </Button>
            </div>
          </div>

          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-red-900">Broadcast to all subscribers</p>
              <p className="text-xs text-red-700">Confirmation required — cannot be undone.</p>
            </div>
            <Button
              onClick={() => onSend({ isTest: false })}
              disabled={isSending || !subscriberCount}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="newsletter-send-broadcast-btn"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {isSending ? 'Sending…' : `Send to ${subscriberCount ?? 0}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterBroadcastModal;
